/**
 * Reglas de los pines de comentario: hilo, menciones, resolver, actividad. Puro.
 * Un pin es un nodo `comentario` cuyo `params.hilo` es la lista de mensajes.
 */
import type { FlowNode, MensajeHilo } from './types';

export interface HiloParams {
  hilo: MensajeHilo[];
  resuelto: boolean;
}

const nuevoIdMsg = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 10) : Math.random().toString(36).slice(2, 12);

/** Acepta los params viejos (`texto` suelto, v1) y los nuevos (`hilo`). */
export function normalizarHilo(params: Record<string, unknown>): HiloParams {
  const hilo = Array.isArray(params.hilo) ? (params.hilo as MensajeHilo[]) : [];
  if (hilo.length === 0 && typeof params.texto === 'string' && params.texto.trim()) {
    return {
      hilo: [
        {
          id: 'v1',
          autorId: typeof params.autorId === 'string' ? params.autorId : '',
          autorNombre: typeof params.autorNombre === 'string' ? params.autorNombre : 'Alguien',
          texto: params.texto.trim(),
          creadoEn: typeof params.creadoEn === 'string' ? params.creadoEn : new Date(0).toISOString(),
        },
      ],
      resuelto: params.resuelto === true,
    };
  }
  return { hilo, resuelto: params.resuelto === true };
}

export function agregarMensaje(params: Record<string, unknown>, msg: { autorId: string; autorNombre: string; texto: string }): Record<string, unknown> {
  const h = normalizarHilo(params);
  const texto = msg.texto.trim();
  if (!texto) return params;
  return { ...params, texto: undefined, hilo: [...h.hilo, { id: nuevoIdMsg(), ...msg, texto, creadoEn: new Date().toISOString() }], resuelto: h.resuelto };
}

export function alternarResuelto(params: Record<string, unknown>): Record<string, unknown> {
  const h = normalizarHilo(params);
  return { ...params, hilo: h.hilo, resuelto: !h.resuelto };
}

// Arrobar a alguien vive en `utils/comentariosUi`: es puro, no sabe nada de un
// flujo, y los comentarios del lanzamiento usan exactamente lo mismo
// (ADR-2026-09-15-arrobar-en-los-comentarios-del-lanzamiento). Se re-exporta
// desde acá para no tocar a quien ya lo importaba.
export { completarArroba, menciones, sugerenciasArroba } from '../../../utils/comentariosUi';

export const esPin = (n: FlowNode): boolean => n.data.tipo === 'comentario';

export interface ResumenHilo {
  id: string;
  ultimo: MensajeHilo | null;
  cantidad: number;
  resuelto: boolean;
  parentId?: string;
}

export function resumirHilos(nodes: FlowNode[]): ResumenHilo[] {
  return nodes.filter(esPin).map((n) => {
    const h = normalizarHilo(n.data.params);
    return { id: n.id, ultimo: h.hilo[h.hilo.length - 1] ?? null, cantidad: h.hilo.length, resuelto: h.resuelto, parentId: n.parentId };
  });
}

/** Actividad: más reciente primero, con búsqueda por texto o autor y filtro de resueltos. */
export function filtrarHilos(hilos: ResumenHilo[], busqueda: string, ocultarResueltos: boolean): ResumenHilo[] {
  const q = busqueda.trim().toLowerCase();
  return hilos
    .filter((h) => h.cantidad > 0)
    .filter((h) => !ocultarResueltos || !h.resuelto)
    .filter((h) => !q || h.ultimo!.texto.toLowerCase().includes(q) || h.ultimo!.autorNombre.toLowerCase().includes(q))
    .sort((a, b) => (b.ultimo?.creadoEn ?? '').localeCompare(a.ultimo?.creadoEn ?? ''));
}
